import clsx from 'clsx';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from 'react-tooltip-lite';
import { trackMixpanelEvent } from 'utils/analytics';
import { formatNumericValue, navigateToUrl } from 'utils/general';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { useSubscribePlan } from '@/modules/payment/apiHooks';
import { EmailUpdate } from '@/modules/payment/subscription/components/EmailUpdate';
import { VerifyEmail } from '@/modules/payment/subscription/components/VerifyEmail';
import { SubscriptionContext } from '@/modules/payment/subscription/context/context';
import { subscriptionActionsTypes } from '@/modules/payment/subscription/context/reducer';
import { handleEnterpriseSubscription } from '@/modules/payment/subscription/logic';
import { IPayment } from '@/modules/payment/types';
import { Button, Typography } from '@/shared/components/atoms';
import { useAuth } from '@/shared/hooks/context/auth';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import InfoCircle from '~/images/shared/ExleminationMark.svg';

const Plan: React.FC<{
  bundle: IPayment.IBundleSchema;
  showDescription: boolean;
}> = ({ bundle, showDescription }) => {
  const { openModal } = useModal();
  // const { push } = useRouter();
  const { t } = useTranslation('payment');
  const {
    state: { intervalType },
    dispatch,
  } = useContext(SubscriptionContext);
  const { user } = useAuth();

  const { mutate: subscribePlan, isLoading } = useSubscribePlan();
  // const { mutate: changeSubcription } = useChangeSubcription();

  const isBusiness: boolean = bundle.bundleName.includes('Business');
  const isOperations: boolean = bundle.bundleName.includes('Operations');
  const isEnterprise: boolean = bundle.bundleName.includes('Enterprise');

  const handleSubscription = () => {
    dispatch({
      type: subscriptionActionsTypes.setPlanId,
      payload: bundle.plans.filter((item) => item.interval === intervalType)[0]
        .stripePriceId,
    });

    if (!isEnterprise) {
      if (user?.email && user.isEmailVerified === 1) {
        subscribePlan(
          bundle.plans.filter((item) => item.interval === intervalType)[0]
            .stripePriceId,
          {
            onSuccess: (data) => navigateToUrl(data.url),
          }
        );
      } else if (!user?.email) {
        openModal(<EmailUpdate />);
      } else if (user?.isEmailVerified !== 1) {
        openModal(<VerifyEmail />);
      }
    } else {
      // TODO: IMP - handle contact us
    }
  };

  // const handleChangeSubscription = () => {
  //   dispatch({
  //     type: subscriptionActionsTypes.setPlanId,
  //     payload: bundle.plans.filter((item) => item.interval === intervalType)[0]
  //       .stripePriceId,
  //   });

  //   if (!isEnterprise) {
  //     if (user?.email && user.isEmailVerified === 1) {
  //       changeSubcription(
  //         bundle.plans.filter((item) => item.interval === intervalType)[0]
  //           .stripePriceId,
  //         {
  //           onSuccess: () => push(routes.billing),
  //         }
  //       );
  //     } else if (!user?.email) {
  //       openModal(<EmailUpdate />);
  //     } else if (user?.isEmailVerified !== 1) {
  //       openModal(<VerifyEmail />);
  //     }
  //   } else {
  //     // TODO: IMP - handle contact us
  //   }
  // };

  return (
    <div
      className={clsx(
        'flex w-1/3 flex-col gap-1 rounded-t-2xl p-16',
        isOperations && 'bg-background-neutral-99',
        isBusiness && 'bg-primary-60',
        isEnterprise && 'bg-background-neutral-99'
      )}
    >
      <span
        className={clsx(
          'ml-auto w-fit rounded-lg px-2 py-1 text-12',
          isBusiness
            ? 'bg-background-neutral-99 text-text-default'
            : 'bg-background-transparent text-background-transparent'
        )}
      >
        {t('most-popular')}
      </span>
      <div className='flex w-full flex-col justify-center gap-2 text-center'>
        <Typography
          variant='h3'
          weight='semiBold'
          className={clsx(isBusiness ? 'text-white' : 'text-text-default')}
        >
          {bundle.bundleName}
        </Typography>
        {showDescription && (
          <Typography
            variant='body2'
            className={clsx(
              isBusiness ? 'text-background-neutral-99' : 'text-surface-10'
            )}
          >
            {bundle.description}
          </Typography>
        )}
      </div>
      <span className={clsx(isEnterprise && 'my-auto')}>
        <Typography
          variant='h2'
          weight='semiBold'
          className={clsx(
            'mb-1 mt-2 flex items-center justify-center gap-1',
            isBusiness ? 'text-white' : 'text-text-default'
          )}
        >
          {isEnterprise ? (
            t('contact-us')
          ) : (
            <>
              {/**
               * INFO: plans is a list of two
               * first one is annual
               * second one is monthly
               */}
              <span
                className={clsx(
                  'flex items-center',
                  intervalType === 'year' && 'line-through opacity-2'
                )}
              >
                {t(
                  `${
                    bundle.plans.filter((item) => item.interval === 'month')[0]
                      .currency
                  }-sign`
                )}
                {
                  bundle.plans.filter((item) => item.interval === 'month')[0]
                    .amount
                }
              </span>
              {intervalType === 'year' && (
                <span className='flex items-center'>
                  {t(
                    `${
                      bundle.plans.filter(
                        (item) => item.interval === intervalType
                      )[0].currency
                    }-sign`
                  )}
                  {formatNumericValue(
                    bundle.plans.filter(
                      (item) => item.interval === intervalType
                    )[0].amount / 12
                  )}
                </span>
              )}
            </>
          )}
        </Typography>
      </span>

      <div
        className={clsx(
          'text-12',
          isBusiness ? 'text-background-neutral-99' : 'text-surface-10'
        )}
      >
        {!isEnterprise && (
          <span className='flex flex-col items-center justify-center'>
            <span className='flex items-center gap-1'>
              <span className='flex items-center gap-4'>
                {t('per-field')}
                <Tooltip
                  content={
                    <Typography
                      variant='caption'
                      className='max-w-[265px] px-16 py-8'
                    >
                      {t('field-explanation')}
                    </Typography>
                  }
                  background={themeColors().background['neutral-99']}
                  className='react-tooltip-rounded'
                  arrow
                  arrowSize={10}
                  direction='bottom'
                  tagName='div'
                  tipContentHover
                  padding={0}
                >
                  <span className='cursor-pointer'>
                    <InfoCircle width='16' height='16' />
                  </span>
                </Tooltip>
                ,
              </span>
              <span>{t('per-month')}</span>
            </span>
            {intervalType === 'year' ? (
              <span>{t('billed-annually')}</span>
            ) : (
              <span>{t('billed-monthly')}</span>
            )}
          </span>
        )}
      </div>

      {/* {user?.subscription === null ? ( */}
      <Button
        className={clsx('!rounded-xl py-2', isBusiness && 'border-none')}
        color={isBusiness ? 'secondary' : 'primary'}
        size='full'
        type='button'
        variant='filled'
        onClick={() => {
          trackMixpanelEvent('Subscribe', user);
          isEnterprise ? handleEnterpriseSubscription() : handleSubscription();
        }}
        loading={isLoading}
      >
        {t(isEnterprise ? 'contact-sales' : 'subscribe')}
      </Button>
      {/* ) : (
        <Button
          className={clsx('!rounded-xl py-2', isBusiness && 'border-none')}
          color={isBusiness ? 'secondary' : 'primary'}
          size='full'
          type='button'
          variant='filled'
          onClick={handleChangeSubscription}
          loading={isLoading}
          disabled={
            user?.subscription?.bundleName === bundle.bundleName &&
            user?.subscription?.interval === intervalType
          }
        >
          {t(
            user?.subscription?.bundleName === bundle.bundleName &&
              user?.subscription?.interval === intervalType
              ? 'current-plan'
              : isEnterprise
              ? 'contact-sales'
              : 'upgrade'
          )}
        </Button>
      )} */}
    </div>
  );
};

export { Plan };
